/**
 * GPS Data Optimization Utilities
 * Provides functions for optimizing GPS data updates and reducing unnecessary processing
 */

import type { DeviceGpsInfo } from '../../gps-websocket/types';
import type { DataProcessingResult } from '../types';

/**
 * Check if device has significant changes that warrant an update
 * @param previous - Previous device state
 * @param current - Current device state
 * @param thresholds - Change detection thresholds
 * @returns True if changes are significant enough to update
 */
export function hasSignificantChange(
  previous: DeviceGpsInfo, 
  current: DeviceGpsInfo, 
  thresholds: { position: number; speed: number }
): boolean {
  // 위치 변경 확인
  const latChange = Math.abs(current.latitude - previous.latitude);
  const lngChange = Math.abs(current.longitude - previous.longitude);
  
  // 속도 변경 확인
  const speedChange = Math.abs((current.speed || 0) - (previous.speed || 0));
  
  // 상태 변경 확인
  const statusChange = current.active !== previous.active || 
                      current.share_video !== previous.share_video;
  
  // 방향 변경 확인
  const directionChange = current.dir_angle !== previous.dir_angle;
  
  // 유의미한 변경사항이 있는지 확인
  return latChange > thresholds.position ||
         lngChange > thresholds.position ||
         speedChange > thresholds.speed ||
         statusChange ||
         directionChange;
}

/**
 * Process GPS data updates and determine what needs to be updated
 * @param previousDevices - Previous device data
 * @param currentDevices - Current device data
 * @param thresholds - Update thresholds
 * @returns Processing result with categorized changes
 */
export function processGpsDataUpdates(
  previousDevices: DeviceGpsInfo[],
  currentDevices: DeviceGpsInfo[],
  thresholds: { position: number; speed: number }
): DataProcessingResult {
  
  // 이전 데이터를 Map으로 변환 (빠른 검색을 위해)
  const previousMap = new Map<string, DeviceGpsInfo>();
  previousDevices.forEach(device => {
    previousMap.set(device.psn, device);
  });
  
  const processed: DeviceGpsInfo[] = [];
  const skipped: DeviceGpsInfo[] = [];
  const added: DeviceGpsInfo[] = [];
  const updated: DeviceGpsInfo[] = [];
  const removedPsns: string[] = [];
  
  // 현재 디바이스들 처리
  currentDevices.forEach(device => {
    const previousDevice = previousMap.get(device.psn);
    
    if (!previousDevice) {
      // 새로 추가된 디바이스
      added.push(device);
      processed.push(device);
    } else if (hasSignificantChange(previousDevice, device, thresholds)) {
      // 유의미한 변경이 있는 디바이스
      updated.push(device);
      processed.push(device);
    } else {
      // 변경사항이 미미한 디바이스 (스킵)
      skipped.push(previousDevice); // 이전 데이터 유지
      processed.push(previousDevice);
    }
    
    // 처리된 디바이스는 맵에서 제거
    previousMap.delete(device.psn);
  });
  
  // 남은 디바이스들은 제거된 것
  previousMap.forEach((device, psn) => {
    removedPsns.push(psn);
  });
  
  // 유의미한 변경사항이 있는지 확인
  const hasSignificantChanges = added.length > 0 || 
                               updated.length > 0 || 
                               removedPsns.length > 0;
  
  return {
    processed,
    skipped,
    added,
    updated,
    removed: removedPsns,
    hasSignificantChanges
  };
}

/**
 * Optimize device data for map rendering
 * Reduces data density while preserving important information
 * @param devices - Device data to optimize
 * @param config - Optimization configuration
 * @returns Optimized device data
 */
export function optimizeForMapRendering(
  devices: DeviceGpsInfo[],
  config: {
    maxDevices?: number;
    prioritizeActive?: boolean;
    prioritizeVideo?: boolean;
    spatialClustering?: boolean;
    clusterDistance?: number;  // meters
  } = {}
): DeviceGpsInfo[] {
  
  let optimized = [...devices];
  
  // 1. 우선순위 기반 정렬
  if (config.prioritizeActive || config.prioritizeVideo) {
    optimized.sort((a, b) => {
      // 활성 디바이스 우선
      if (config.prioritizeActive) {
        if (a.active === 'on' && b.active !== 'on') return -1;
        if (a.active !== 'on' && b.active === 'on') return 1;
      }
      
      // 비디오 공유 디바이스 우선
      if (config.prioritizeVideo) {
        if (a.share_video === 'on' && b.share_video !== 'on') return -1;
        if (a.share_video !== 'on' && b.share_video === 'on') return 1;
      }
      
      return 0;
    });
  }
  
  // 2. 최대 디바이스 수 제한
  if (config.maxDevices && optimized.length > config.maxDevices) {
    optimized = optimized.slice(0, config.maxDevices);
  }
  
  // 3. 공간 클러스터링 (선택사항)
  if (config.spatialClustering && config.clusterDistance) {
    optimized = applySpatialClustering(optimized, config.clusterDistance);
  }
  
  return optimized;
}

/**
 * Apply spatial clustering to reduce marker density
 * @param devices - Device data
 * @param clusterDistance - Clustering distance in meters
 * @returns Clustered device data
 */
function applySpatialClustering(
  devices: DeviceGpsInfo[], 
  clusterDistance: number
): DeviceGpsInfo[] {
  
  const clustered: DeviceGpsInfo[] = [];
  const processed = new Set<string>();
  
  devices.forEach(device => {
    if (processed.has(device.psn)) return;
    
    // 현재 디바이스를 중심으로 클러스터 형성
    const cluster = [device];
    processed.add(device.psn);
    
    // 주변 디바이스들 찾기
    devices.forEach(otherDevice => {
      if (processed.has(otherDevice.psn)) return;
      
      const distance = calculateDistance(
        device.latitude, device.longitude,
        otherDevice.latitude, otherDevice.longitude
      );
      
      if (distance <= clusterDistance) {
        cluster.push(otherDevice);
        processed.add(otherDevice.psn);
      }
    });
    
    // 클러스터에서 가장 중요한 디바이스 선택
    const representative = selectClusterRepresentative(cluster);
    clustered.push(representative);
  });
  
  return clustered;
}

/**
 * Select the most representative device from a cluster
 * @param cluster - Cluster of devices
 * @returns Most representative device
 */
function selectClusterRepresentative(cluster: DeviceGpsInfo[]): DeviceGpsInfo {
  // 우선순위: 활성 > 비디오 공유 > 최신 위치
  const sorted = cluster.sort((a, b) => {
    // 활성 상태 우선
    if (a.active === 'on' && b.active !== 'on') return -1;
    if (a.active !== 'on' && b.active === 'on') return 1;
    
    // 비디오 공유 우선
    if (a.share_video === 'on' && b.share_video !== 'on') return -1;
    if (a.share_video !== 'on' && b.share_video === 'on') return 1;
    
    // 속도가 빠른 것 우선 (움직이는 것)
    const speedA = a.speed || 0;
    const speedB = b.speed || 0;
    return speedB - speedA;
  });
  
  return sorted[0];
}

/**
 * Calculate distance between two GPS coordinates using Haversine formula
 * @param lat1 - Latitude of first point
 * @param lng1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lng2 - Longitude of second point
 * @returns Distance in meters
 */
function calculateDistance(
  lat1: number, lng1: number, 
  lat2: number, lng2: number
): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Convert degrees to radians
 * @param degrees - Angle in degrees
 * @returns Angle in radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Calculate data processing performance metrics
 * @param startTime - Processing start time
 * @param result - Processing result
 * @returns Performance metrics
 */
export function calculateProcessingMetrics(
  startTime: number,
  result: DataProcessingResult
): {
  processingTime: number;
  optimizationRatio: number;
  throughput: number;
} {
  const processingTime = Date.now() - startTime;
  const totalDevices = result.processed.length + result.skipped.length;
  const optimizationRatio = totalDevices > 0 ? (result.skipped.length / totalDevices) * 100 : 0;
  const throughput = totalDevices / (processingTime / 1000); // devices per second
  
  return {
    processingTime,
    optimizationRatio,
    throughput
  };
}

/**
 * Batch process multiple device updates
 * @param updates - Array of device update batches
 * @param thresholds - Processing thresholds
 * @returns Consolidated processing result
 */
export function batchProcessUpdates(
  updates: DeviceGpsInfo[][],
  thresholds: { position: number; speed: number }
): DataProcessingResult {
  
  // 모든 업데이트를 하나로 합치기
  const allCurrentDevices = updates.flat();
  
  // 중복 제거 (최신 데이터 우선)
  const deviceMap = new Map<string, DeviceGpsInfo>();
  allCurrentDevices.forEach(device => {
    deviceMap.set(device.psn, device);
  });
  
  const consolidatedDevices = Array.from(deviceMap.values());
  
  // 빈 이전 상태로 처리 (배치의 첫 번째로 간주)
  return processGpsDataUpdates([], consolidatedDevices, thresholds);
}